import { useState, useEffect, useRef } from "react";
import axios from "axios";

import { Logger } from "aws-amplify";
import { useCart } from "../../context/cart-context";
import { useLocation, useNavigate } from "react-router-dom";
import {
    calculateTaxAmount,
    cleanupPhoneNumber,
    convertCentsToDollars,
    getCartProductUnitTotalPrice,
    isItemSoldOut,
    isThemePreviewMode,
    sendCustomEmail,
} from "../../util/util";
import { useMutation } from "@apollo/client";
import { CREATE_ORDER, UPDATE_ORDER } from "../../graphql/customMutations";
import {
    EPromotionType,
    IGET_RESTAURANT_AVAILABILITY_MODIFIER,
    IGET_RESTAURANT_AVAILABILITY_PRODUCT,
    IGET_RESTAURANT_PRODUCT,
    IGET_RESTAURANT_AVAILABILITY_CATEGORY,
    IGET_RESTAURANT_AVAILABILITY_RESTAURANT,
    IGET_RESTAURANT_CATEGORY,
} from "../../graphql/customQueries";
import { checkoutPath, orderPlacedPath, restaurantListPath, windcavePaymentCallbackPath } from "../main";
import { ProductModal } from "../modals/product";
import {
    ICartProduct,
    IPreSelectedModifiers,
    ICartModifierGroup,
    EPaymentMethod,
    EOrderType,
    ICartPayment,
    ICartPaymentAmounts,
    EPaymentModalState,
} from "../../model/model";
import { useUser } from "../../context/user-context";
import { PageWrapper } from "../../tabin/components/pageWrapper";
import { Button } from "../../tabin/components/button";
import { useRegister } from "../../context/register-context";
import { toLocalISOString } from "../../util/util";
import { useRestaurant } from "../../context/restaurant-context";
import { TextArea } from "../../tabin/components/textArea";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Link } from "../../tabin/components/link";
import { Auth } from "./auth/auth";
import { CloseIcon } from "../../tabin/components/icons/closeIcon";
import { toast } from "../../tabin/components/toast";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import { isMobile } from "react-device-detect";
import { OrderScheduleDateTime } from "../../tabin/components/orderScheduleDateTime";
import { useErrorLogging } from "../../context/errorLogging-context";
import { PromotionCodeModal } from "../modals/promotionCodeModal";
import { OrderThresholdMessageModal } from "../modals/orderThresholdMessageModal";
import { ProductModifier } from "../shared/productModifier";
import { Radio } from "../../tabin/components/radio";
import { Stepper } from "../../tabin/components/stepper";
import { OrderTypeModal } from "../modals/orderTypeModal";
import { useAlert } from "../../tabin/components/alert";

import { useGetRestaurantAvailabilityLazyQuery } from "../../hooks/useGetRestaurantAvailabilityLazyQuery";

import "./checkout.scss";

const logger = new Logger("checkout");
const ONLINE_PAYMENT_FAILED_PREFIX = "ONLINE_PAYMENT_FAILED";
const ONLINE_PAYMENT_SUCCESS_TYPE = "ONLINE_PAYMENT_SUCCESS";
const EFTPOS_LOG_RETENTION_SECONDS = 90 * 24 * 60 * 60;

const getGatewayFailureReason = (reason: unknown, fallback: string) => {
    if (typeof reason === "string" && reason.trim()) return reason.trim();
    if (reason && typeof reason === "object" && "message" in reason && typeof (reason as { message?: unknown }).message === "string") {
        const message = (reason as { message: string }).message.trim();
        if (message) return message;
    }
    return fallback;
};

const getOnlinePaymentFailedCancellationReason = (reason: unknown, fallback: string) =>
    `${ONLINE_PAYMENT_FAILED_PREFIX} - ${getGatewayFailureReason(reason, fallback)}`;

type AvailabilityCheckResult = {
    soldOutItems: string[];
    productsToRemove: number[];
    productsToUpdate: { index: number; product: ICartProduct }[];
};

type AvailabilityModifierGroupIndex = {
    modifiersById: Map<string, IGET_RESTAURANT_AVAILABILITY_MODIFIER>;
};

type AvailabilityProductIndex = {
    product: IGET_RESTAURANT_AVAILABILITY_PRODUCT;
    modifierGroupsById: Map<string, AvailabilityModifierGroupIndex>;
};

type AvailabilityCategoryIndex = {
    category: IGET_RESTAURANT_AVAILABILITY_CATEGORY;
    productsById: Map<string, AvailabilityProductIndex>;
};

// Component
export const Checkout = () => {
    const navigate = useNavigate();

    // context
    const {
        orderType,
        products,
        notes,
        setNotes,
        tableNumber,
        clearCart,
        promotion,
        total,
        surcharge,
        subTotal,
        updateProduct,
        updateProductQuantity,
        deleteProduct,
        addProduct,
        userAppliedPromotionCode,
        removeUserAppliedPromotion,
        setIsShownUpSellCrossSellModal,
        isShownOrderThresholdMessageModal,
        setIsShownOrderThresholdMessageModal,
        orderScheduledAt,
        updateOrderScheduledAt,
        guestInformation,
        paymentMethod,
        setPaymentMethod,
        covers,
        setCovers,
        bugFixRecalculateTotal,
        bugFixRecalculateSubTotalAndSurcharge,
        deliveryAddress,
        deliveryNotes,
        deliveryProvider,
        deliveryDistanceMeters,
        deliveryFeeDiscount,
        deliveryFee,
        getUberDirectDeliveryPayload,
    } = useCart();
    const { restaurant } = useRestaurant();
    const { logError, addEftposLog } = useErrorLogging();
    const { showAlert } = useAlert();

    const [showSpinner, setShowSpinner] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    const [createOrderMutation] = useMutation(CREATE_ORDER, {
        update: (proxy, mutationResult) => {
            logger.debug("mutation result: ", mutationResult);
        },
    });

    const [updateOrderMutation] = useMutation(UPDATE_ORDER, {
        update: (proxy, mutationResult) => {
            logger.debug("mutation result: ", mutationResult);
        },
    });

    const cancelPendingPaymentOrder = async (input: {
        orderId: string;
        placedAt: string;
        provider: "STRIPE" | "WINDCAVE";
        amount: number;
        reason: unknown;
        fallbackReason: string;
        rawGatewayResponse: unknown;
        failureStep: string;
    }) => {
        const now = new Date();
        const cancellationReason = getOnlinePaymentFailedCancellationReason(input.reason, input.fallbackReason);

        await updateOrderMutation({
            variables: {
                orderId: input.orderId,
                status: "CANCELLED",
                paid: false,
                paymentInProgress: false,
                onlineOrder: true,
                cancellationReason,
                placedAt: input.placedAt,
                cancelledAt: toLocalISOString(now),
                cancelledAtUtc: now.toISOString(),
            },
        });

        if (restaurant) {
            await addEftposLog({
                eftposProvider: input.provider,
                amount: input.amount,
                type: ONLINE_PAYMENT_FAILED_PREFIX,
                payload: JSON.stringify({
                    orderId: input.orderId,
                    provider: input.provider,
                    cancellationReason,
                    rawGatewayResponse: input.rawGatewayResponse,
                    failureStep: input.failureStep,
                }),
                restaurantId: restaurant.id,
                timestamp: toLocalISOString(now),
                expiry: Math.floor(now.getTime() / 1000) + EFTPOS_LOG_RETENTION_SECONDS,
            });
        }
    };

    const { getRestaurantDataAvailability } = useGetRestaurantAvailabilityLazyQuery();

    // state
    const [productToEdit, setProductToEdit] = useState<{
        product: ICartProduct;
        displayOrder: number;
    } | null>(null);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [showPromotionCodeModal, setShowPromotionCodeModal] = useState(false);
    const [showOrderThresholdMessageModal, setShowOrderThresholdMessageModal] = useState(false);
    const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);

    const authSectionRef = useRef<HTMLDivElement>(null);
    const orderTimeSectionRefionRef = useRef<HTMLDivElement>(null);

    // const [showItemUpdatedModal, setShowItemUpdatedModal] = useState(false);
    const [showFullScreenSpinner, setShowFullScreenSpinner] = useState(false);

    const { register } = useRegister();

    useEffect(() => {
        if (showEditProductModal) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [showEditProductModal]);

    if (!restaurant) {
        navigate(restaurantListPath);
        return <div>Invalid restaurant</div>;
        // throw "Restaurant is invalid";
    }

    if (!register) {
        throw "Register is not valid";
    }

    // callbacks
    const onClosePromotionCodeModal = () => {
        setShowPromotionCodeModal(false);
    };

    const onCloseEditProductModal = () => {
        setProductToEdit(null);
        setShowEditProductModal(false);
    };

    const onCloseOrderThresholdMessageModal = () => {
        setShowOrderThresholdMessageModal(false);
    };

    const onCloseOrderTypeModal = () => {
        setShowOrderTypeModal(false);
    };

    // const onCloseItemUpdatedModal = () => {
    //     setShowItemUpdatedModal(false);
    // };

    const onEditProduct = (product: ICartProduct, displayOrder: number) => {
        setProductToEdit({ product, displayOrder });
        setShowEditProductModal(true);
    };

    const onUpdateProductQuantity = (displayOrder: number, productQuantity: number) => {
        updateProductQuantity(displayOrder, productQuantity);
    };

    const onRemoveProduct = (displayOrder: number) => {
        deleteProduct(displayOrder);
    };

    const onNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value);
    };

    // submit callback
    const createOrder = async (bugFixTotal: number, bugFixSubTotal: number, bugFixSurcharge: number, stripePaymentId?: string, paymentInProgress?: boolean) => {
        const now = new Date();

        if (isThemePreviewMode()) {
            throw "Ordering is disabled in theme preview mode";
        }

        // if (!user) {
        //     await logError(
        //         JSON.stringify({
        //             error: "Invalid user",
        //             context: { orderRestaurantId: restaurant.id },
        //         })
        //     );
        //     throw "Invalid user";
        // }
        if (!orderType) {
            await logError(
                "Invalid order type",
                JSON.stringify({
                    error: "Invalid order type",
                    context: { orderRestaurantId: restaurant.id, orderType: orderType },
                }),
            );
            throw "Invalid order type";
        }
        if (!restaurant) {
            await logError("Invalid restaurant", JSON.stringify({ restaurant: restaurant }));
            throw "Invalid restaurant";
        }
        if (!products || products.length == 0) {
            await logError(
                "No products have been selected",
                JSON.stringify({
                    error: "No products have been selected",
                    context: { orderRestaurantId: restaurant.id },
                }),
            );
            throw "No products have been selected";
        }
        let variables;
        try {
            variables = {
                country: restaurant.country,
                status: "NEW",
                paid: paymentInProgress ? false : true,
                // cashPayment: false,
                // TEMP: stays false until payment confirmed so old electron apps (filtered by
                // onlineOrder:true) don't print unconfirmed receipts. Remove once all apps updated.
                onlineOrder: paymentInProgress ? false : true,
                guestCheckout: true, //TODO: Update later when actual auth is added
                paymentInProgress: paymentInProgress,
                stripePaymentId: stripePaymentId,
                orderScheduledAt: orderScheduledAt,
                orderScheduledAtUtc: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null,
                customerInformation: {
                    firstName: guestInformation.firstName || "",
                    email: guestInformation.email || "",
                    phoneNumber: cleanupPhoneNumber(guestInformation.phoneNumber) || "",
                },
                type: orderType,
                number: tableNumber ? "QR" : "Web", //TODO: Remove later on
                covers: register && register.enableCovers ? covers : undefined,
                table: tableNumber,
                paymentAmounts: { cash: 0, eftpos: 0, online: stripePaymentId ? bugFixSubTotal : 0 },
                payments: stripePaymentId
                    ? {
                          type: "ONLINE",
                          amount: bugFixSubTotal,
                      }
                    : undefined,
                notes: notes,
                thirdPartyDeliveryId: "",
                deliveryProvider: orderType === EOrderType.DELIVERY ? deliveryProvider : undefined,
                deliveryAddress:
                    orderType === EOrderType.DELIVERY && deliveryAddress
                        ? `${deliveryAddress.street_address.join(", ")}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zip_code}`
                        : "No address available",
                deliveryNotes: orderType === EOrderType.DELIVERY ? deliveryNotes : undefined,
                deliveryDistanceMeters: orderType === EOrderType.DELIVERY ? deliveryDistanceMeters : undefined,
                deliveryFeeDiscount: orderType === EOrderType.DELIVERY ? deliveryFeeDiscount : undefined,
                deliveryFee: orderType === EOrderType.DELIVERY ? deliveryFee : undefined,
                deliveryTrackingUrl: "",
                uberDirectDeliveryPayload: orderType === EOrderType.DELIVERY ? getUberDirectDeliveryPayload() : undefined,
                total: bugFixTotal,
                surcharge: bugFixSurcharge || undefined,
                discount: promotion ? promotion.discountedAmount : undefined,
                promotionId: promotion ? promotion.promotion.id : undefined,
                promotionType: promotion ? promotion.promotion.type : undefined,
                tax: Math.round(calculateTaxAmount(restaurant.country, bugFixSubTotal)),
                subTotal: bugFixSubTotal,
                registerId: register.id,
                products: JSON.parse(JSON.stringify(products)) as ICartProduct[], // copy obj so we can mutate it later
                placedAt: toLocalISOString(now),
                placedAtUtc: now.toISOString(),
                orderBatchable: register.orderBatchWindowInSeconds ? 1 : 0,
                orderBatchWindowInSeconds: register.orderBatchWindowInSeconds || undefined,
                orderUserId: restaurant.restaurantManagerId, //TODO: Update later when actual auth is added
                orderRestaurantId: restaurant.id,
            };
            if (restaurant.autoCompleteOrders) {
                variables.status = "COMPLETED";
                //@ts-ignore
                variables.completedAt = toLocalISOString(now);
                //@ts-ignore
                variables.completedAtUtc = now.toISOString();
                variables.paid = true;
            }
        } catch (e) {
            await logError(
                "Error in createOrderMutation input",
                JSON.stringify({
                    error: "No products have been selected",
                    context: {
                        country: restaurant.country,
                        status: "NEW",
                        paid: true,
                        // cashPayment: false,
                        onlineOrder: true,
                        guestCheckout: true, //TODO: Update later when actual auth is added
                        stripePaymentId: stripePaymentId,
                        orderScheduledAt: orderScheduledAt,
                        orderScheduledAtUtc: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null,
                        customerInformation: {
                            firstName: guestInformation.firstName || "",
                            email: guestInformation.email || "",
                            phoneNumber: cleanupPhoneNumber(guestInformation.phoneNumber) || "",
                        },
                        type: orderType,
                        number: tableNumber ? "QR" : "Web", //TODO: Remove later on
                        covers: register && register.enableCovers ? covers : undefined,
                        table: tableNumber,
                        notes: notes,
                        paymentAmounts: { cash: 0, eftpos: 0, online: bugFixSubTotal },
                        payments: {
                            type: "ONLINE",
                            amount: bugFixSubTotal,
                        },
                        deliveryProvider: orderType === EOrderType.DELIVERY ? deliveryProvider : undefined,
                        deliveryAddress:
                            orderType === EOrderType.DELIVERY && deliveryAddress
                                ? `${deliveryAddress.street_address.join(", ")}, ${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zip_code}`
                                : "No address available",
                        deliveryNotes: orderType === EOrderType.DELIVERY ? deliveryNotes : undefined,
                        deliveryDistanceMeters: orderType === EOrderType.DELIVERY ? deliveryDistanceMeters : undefined,
                        deliveryFeeDiscount: orderType === EOrderType.DELIVERY ? deliveryFeeDiscount : undefined,
                        deliveryFee: orderType === EOrderType.DELIVERY ? deliveryFee : undefined,
                        total: bugFixTotal,
                        discount: promotion ? promotion.discountedAmount : undefined,
                        promotionId: promotion ? promotion.promotion.id : undefined,
                        promotionType: promotion ? promotion.promotion.type : undefined,
                        tax: Math.round(calculateTaxAmount(restaurant.country, bugFixSubTotal)),
                        subTotal: bugFixSubTotal,
                        surcharge: bugFixSurcharge || undefined,
                        registerId: register.id,
                        products: JSON.parse(JSON.stringify(products)) as ICartProduct[], // copy obj so we can mutate it later
                        placedAt: toLocalISOString(now),
                        placedAtUtc: now.toISOString(),
                        orderBatchable: register.orderBatchWindowInSeconds ? 1 : 0,
                        orderBatchWindowInSeconds: register.orderBatchWindowInSeconds || undefined,
                        orderUserId: restaurant.restaurantManagerId, //TODO: Update later when actual auth is added
                        orderRestaurantId: restaurant.id,
                    },
                }),
            );
            throw "Error in createOrderMutation input";
        }
        try {
            if (notes == null || notes == "") {
                //@ts-ignore
                delete variables.notes;
            }
            variables.products.forEach((product) => {
                if (product.modifierGroups.length == 0) {
                    //@ts-ignore
                    delete product.modifierGroups;
                }
                if (product.image == null) {
                    //@ts-ignore
                    delete product.image;
                }
                if (product.notes == null || product.notes == "") {
                    //@ts-ignore
                    delete product.notes;
                }
                if (product.category.image == null) {
                    //@ts-ignore
                    delete product.category.image;
                }
                if (product.reportingGroup == null || product.reportingGroup == "") {
                    //@ts-ignore
                    delete product.reportingGroup;
                }
            });
            // process order
            const res = await createOrderMutation({
                variables: variables,
            });
            logger.debug("process order mutation result: ", res);

            return res.data.createOrder;
        } catch (e) {
            console.log("process order mutation error: ", e);

            await logError(
                e,
                JSON.stringify({
                    error: e,
                    variables: variables,
                    userAgent: navigator.userAgent,
                    timeSincePageLoadMs: Math.round(performance.now()),
                }),
            );
            throw e;
        }
    };

    const checkConditionsBeforeCreateOrder = (
        latestRestaurant: IGET_RESTAURANT_AVAILABILITY_RESTAURANT | null | undefined,
        cartProducts: ICartProduct[] | null,
    ): AvailabilityCheckResult => {
        if (!latestRestaurant || !cartProducts) {
            return { soldOutItems: [], productsToRemove: [], productsToUpdate: [] };
        }

        const soldOutItems = new Set<string>();
        const productsToRemove = new Set<number>();
        const productsToUpdate: { index: number; product: ICartProduct }[] = [];

        const categoriesById = new Map<string, AvailabilityCategoryIndex>();
        for (const category of latestRestaurant.categories.items) {
            const productsById = new Map<string, AvailabilityProductIndex>();

            for (const productItem of category.products.items) {
                const product = productItem.product;
                const modifierGroupsById = new Map<string, AvailabilityModifierGroupIndex>();

                for (const modifierGroupItem of product.modifierGroups.items) {
                    const modifierGroup = modifierGroupItem.modifierGroup;
                    const modifiersById = new Map(modifierGroup.modifiers.items.map((modifierItem) => [modifierItem.modifier.id, modifierItem.modifier]));

                    modifierGroupsById.set(modifierGroup.id, {
                        modifiersById,
                    });
                }

                productsById.set(product.id, {
                    product,
                    modifierGroupsById,
                });
            }

            categoriesById.set(category.id, {
                category,
                productsById,
            });
        }

        for (let index = 0; index < cartProducts.length; index++) {
            const cartProduct = cartProducts[index];
            let updatedProduct: ICartProduct | null = null;

            const addSoldOutItem = (name: string) => {
                soldOutItems.add(name);
            };

            const markProductForRemoval = (name: string) => {
                addSoldOutItem(name);
                productsToRemove.add(index);
            };

            const ensureUpdatedProduct = (): ICartProduct => {
                if (!updatedProduct) {
                    updatedProduct = JSON.parse(JSON.stringify(cartProduct)) as ICartProduct;
                }
                return updatedProduct;
            };

            const removeModifierGroupFromProduct = (modifierGroupId: string) => {
                const productCopy = ensureUpdatedProduct();
                productCopy.modifierGroups = productCopy.modifierGroups.filter((group) => group.id !== modifierGroupId);
            };

            if (!cartProduct.category) {
                markProductForRemoval(cartProduct.name);
                continue;
            }

            const categoryIndex = categoriesById.get(cartProduct.category.id);
            if (!categoryIndex) {
                markProductForRemoval(cartProduct.name);
                continue;
            }

            const isCategorySoldOut = isItemSoldOut(categoryIndex.category.soldOut ?? undefined, categoryIndex.category.soldOutDate ?? undefined);
            if (isCategorySoldOut) {
                markProductForRemoval(cartProduct.name);
                continue;
            }

            const productIndex = categoryIndex.productsById.get(cartProduct.id);
            if (!productIndex) {
                markProductForRemoval(cartProduct.name);
                continue;
            }

            const product = productIndex.product;
            const isProductSoldOut = isItemSoldOut(product.soldOut ?? undefined, product.soldOutDate ?? undefined);
            const productAvailableQuantity = isProductSoldOut ? 0 : product.totalQuantityAvailable;
            if (isProductSoldOut || (productAvailableQuantity !== null && productAvailableQuantity < cartProduct.quantity)) {
                addSoldOutItem(product.name);
                if (productAvailableQuantity !== null && productAvailableQuantity > 0) {
                    const productCopy = ensureUpdatedProduct();
                    productCopy.quantity = productAvailableQuantity;
                } else {
                    productsToRemove.add(index);
                    continue;
                }
            }

            for (const mg of cartProduct.modifierGroups) {
                const modifierGroupIndex = productIndex.modifierGroupsById.get(mg.id);
                if (!modifierGroupIndex) {
                    removeModifierGroupFromProduct(mg.id);
                    continue;
                }

                let shouldRemoveModifierGroup = false;
                for (const m of mg.modifiers) {
                    const modifier = modifierGroupIndex.modifiersById.get(m.id);
                    if (!modifier) {
                        addSoldOutItem(m.name);
                        shouldRemoveModifierGroup = true;
                        continue;
                    }

                    const isModifierSoldOut = isItemSoldOut(modifier.soldOut ?? undefined, modifier.soldOutDate ?? undefined);
                    const modifierAvailableQuantity = isModifierSoldOut ? 0 : modifier.totalQuantityAvailable;
                    if (isModifierSoldOut || (modifierAvailableQuantity !== null && modifierAvailableQuantity < m.quantity)) {
                        addSoldOutItem(modifier.name);
                        shouldRemoveModifierGroup = true;
                    }
                }

                if (shouldRemoveModifierGroup) {
                    removeModifierGroupFromProduct(mg.id);
                }
            }

            const productToUpdate = !productsToRemove.has(index) ? (updatedProduct as ICartProduct | null) : null;
            if (productToUpdate) {
                productToUpdate.totalPrice = getCartProductUnitTotalPrice(productToUpdate);
                productsToUpdate.push({ index, product: productToUpdate });
            }
        }

        return {
            soldOutItems: Array.from(soldOutItems),
            productsToRemove: Array.from(productsToRemove),
            productsToUpdate,
        };
    };

    const onCreateTransaction = async () => {
        if (isThemePreviewMode()) {
            toast.error("Ordering is disabled in theme preview mode.");
            return;
        }

        // if (!user) {
        //     toast.error("You must log in before placing an order.");
        //     return;
        // }

        if (restaurant.orderThresholds?.enable && restaurant.orderThresholdMessage && !isShownOrderThresholdMessageModal) {
            setShowOrderThresholdMessageModal(true);
            return;
        }

        if (!orderType) {
            toast.error("Please select a valid order type");
            return;
        }

        if (!paymentMethod) {
            toast.error("Please select a payment type");
            return;
        }

        if ((orderScheduledAt == "INVALID" || !orderScheduledAt) && !tableNumber) {
            orderTimeSectionRefionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please select a valid order time before placing an order.");
            return;
        }

        if (!guestInformation.email || guestInformation.emailError) {
            authSectionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please provide a valid email address before placing an order.");
            return;
        }

        if (
            tableNumber === null &&
            (!guestInformation.firstName || guestInformation.firstNameError || !guestInformation.phoneNumber || guestInformation.phoneNumberError)
        ) {
            authSectionRef.current?.scrollIntoView({ behavior: "smooth" });
            toast.error("Please fill out all customer information before placing an order.");
            return;
        }

        if (orderType === EOrderType.DELIVERY && !deliveryAddress) {
            setShowOrderTypeModal(true);
            toast.error("Please enter a valid delivery address.");
            return;
        }

        if (!stripe || !elements) {
            toast.error("Failed to configure Stripe correctly. Please try again later.");
            return;
        }

        if (restaurant.onlinePaymentGatewayProvider === "STRIPE" && !restaurant.stripeAccountId) {
            alert("This restaurant has not yet been setup with online ordering. Please contact a Tabin representative!");
            return;
        }

        try {
            console.log("on submit order called with: ", register, register.checkConditionsBeforeCreateOrder);
            // Check backend quantities before creating order
            if (register.checkConditionsBeforeCreateOrder) {
                setShowFullScreenSpinner(true);

                const { data: restaurantData } = await getRestaurantDataAvailability({
                    variables: { restaurantId: restaurant.id },
                    fetchPolicy: "no-cache", //This is to stop the GetRestaurant API get double called. I think its somehting to do with useGetRestaurantQuery "cache-first" and "netowrk-first" fetchPolicy.
                });
                const { soldOutItems, productsToRemove, productsToUpdate } = checkConditionsBeforeCreateOrder(restaurantData?.getRestaurant, products);

                setShowFullScreenSpinner(false);

                if (soldOutItems.length > 0) {
                    // setShowPaymentModal(false);
                    // setPaymentModalState(EPaymentModalState.None);
                    // setPaymentOutcomeOrderNumber(null);
                    showAlert(
                        "Items Unavailable",
                        `The following items are no longer available:\n${soldOutItems.join("\n")}`,
                        null,
                        () => {
                            const removeIndexes = productsToRemove.slice().sort((a, b) => b - a);
                            for (let i = 0; i < productsToUpdate.length; i++) {
                                const update = productsToUpdate[i];
                                if (removeIndexes.includes(update.index)) continue;
                                updateProduct(update.index, update.product);
                            }
                            removeIndexes.forEach((removeIndex) => {
                                deleteProduct(removeIndex);
                            });
                        },
                        null,
                        "Review Order",
                    );
                    return;
                }
            }

            setShowSpinner(true);

            const bugFixTotal = bugFixRecalculateTotal(products);
            const bugFixSubTotalandSurcharge = bugFixRecalculateSubTotalAndSurcharge(bugFixTotal);
            const bugFixSubTotal = bugFixSubTotalandSurcharge.subTotal;
            const bugFixSurcharge = bugFixSubTotalandSurcharge.surcharge;

            let createdOrderId = "";

            if (paymentMethod === EPaymentMethod.EFTPOS) {
                if (restaurant.onlinePaymentGatewayProvider === "WINDCAVE") {
                    const callbackBase = `${window.location.origin}${windcavePaymentCallbackPath}`;

                    // Create PAYMENTINPROGRESS order before redirecting to Windcave HPP.
                    // This ensures the order exists even if the browser closes during payment.
                    const pendingOrder = await createOrder(bugFixTotal, bugFixSubTotal, bugFixSurcharge, undefined, true);
                    const createdOrderId = pendingOrder.id;

                    // Store minimal data needed by WindcavePaymentCallback to update the order after redirect
                    sessionStorage.setItem(
                        "windcavePending",
                        JSON.stringify({
                            orderId: createdOrderId,
                            placedAt: pendingOrder.placedAt,
                            amount: bugFixSubTotal,
                            restaurantId: restaurant.id,
                            orderType,
                            tableNumber,
                            orderScheduledAt,
                        }),
                    );

                    const { data: paymentIntentData } = await axios.post(process.env.REACT_APP_CREATE_ONLINE_PAYMENT_URL!, {
                        amount: bugFixSubTotal,
                        restaurantId: restaurant.id,
                        onlinePaymentGatewayProvider: "WINDCAVE",
                        windcaveApprovedUrl: `${callbackBase}?status=approved`,
                        windcaveDeclinedUrl: `${callbackBase}?status=declined`,
                        windcaveCancelledUrl: `${callbackBase}?status=cancelled`,
                        windcaveMerchantReference: createdOrderId,
                        orderType: orderType,
                        deliveryFee: deliveryFee,
                        deliveryFeeDiscount: deliveryFeeDiscount,
                    });

                    const windcaveRedirectUrl = paymentIntentData.windcaveRedirectUrl;
                    console.log("xxx...windcaveRedirectUrl", windcaveRedirectUrl);
                    window.location.href = windcaveRedirectUrl;

                    return;
                } else {
                    const element = elements.getElement(CardElement);
                    if (!element) return;

                    const paymentMethodResult = await stripe.createPaymentMethod({
                        type: "card",
                        card: element,
                    });

                    if (paymentMethodResult.error) throw paymentMethodResult.error.message || "Your card has been declined.";

                    const stripePaymentMethodId = paymentMethodResult.paymentMethod.id;

                    // Create the order first (paymentInProgress: true) so the orderId can be
                    // embedded in the Stripe payment intent metadata for the webhook fallback.
                    const pendingOrder = await createOrder(bugFixTotal, bugFixSubTotal, bugFixSurcharge, undefined, true);
                    const pendingOrderId = pendingOrder.id;
                    const pendingOrderPlacedAt = pendingOrder.placedAt;
                    createdOrderId = pendingOrderId;

                    const { data: stripeIntentData } = await axios.post(process.env.REACT_APP_CREATE_ONLINE_PAYMENT_URL!, {
                        amount: bugFixSubTotal,
                        restaurantId: restaurant.id,
                        orderId: pendingOrderId,
                        stripePaymentMethodId: stripePaymentMethodId,
                        onlinePaymentGatewayProvider: "STRIPE",
                        orderType: orderType,
                        deliveryFee: deliveryFee,
                        deliveryFeeDiscount: deliveryFeeDiscount,
                    });

                    const clientSecret = stripeIntentData.clientSecret;

                    const cardPayment = await stripe.confirmCardPayment(clientSecret, {
                        payment_method: stripePaymentMethodId,
                    });

                    if (cardPayment.error) {
                        await cancelPendingPaymentOrder({
                            orderId: pendingOrderId,
                            placedAt: pendingOrderPlacedAt,
                            provider: "STRIPE",
                            amount: bugFixSubTotal,
                            reason: cardPayment.error.message,
                            fallbackReason: "Payment declined",
                            rawGatewayResponse: {
                                code: cardPayment.error.code,
                                declineCode: cardPayment.error.decline_code,
                                message: cardPayment.error.message,
                                paymentIntentId:
                                    typeof cardPayment.error.payment_intent === "string"
                                        ? cardPayment.error.payment_intent
                                        : cardPayment.error.payment_intent?.id,
                                type: cardPayment.error.type,
                            },
                            failureStep: "confirmCardPayment",
                        });
                        throw cardPayment.error.message || "Your card has been declined.";
                    }

                    const paymentIntentId = cardPayment.paymentIntent.id;
                    console.log(`PaymentIntent (${paymentIntentId}): ${cardPayment.paymentIntent.status}`);

                    const { data: confirmData } = await axios.post(process.env.REACT_APP_CONFIRM_ONLINE_PAYMENT_STRIPE_URL!, {
                        orderId: pendingOrderId,
                        paymentIntentId,
                    });

                    if (!confirmData.confirmed) {
                        await cancelPendingPaymentOrder({
                            orderId: pendingOrderId,
                            placedAt: pendingOrderPlacedAt,
                            provider: "STRIPE",
                            amount: bugFixSubTotal,
                            reason: confirmData.reason || confirmData.error,
                            fallbackReason: "Payment could not be confirmed",
                            rawGatewayResponse: {
                                ...confirmData,
                                paymentIntentId,
                            },
                            failureStep: "confirmOnlinePaymentStripe",
                        });
                        throw "Payment could not be confirmed. Please try again or contact support.";
                    }

                    if (restaurant) {
                        const now = new Date();
                        await addEftposLog({
                            eftposProvider: "STRIPE",
                            amount: bugFixSubTotal,
                            type: ONLINE_PAYMENT_SUCCESS_TYPE,
                            payload: JSON.stringify({
                                orderId: pendingOrderId,
                                provider: "STRIPE",
                                paymentIntentId,
                                rawGatewayResponse: {
                                    cardPaymentStatus: cardPayment.paymentIntent.status,
                                    confirmData,
                                },
                                successStep: "confirmOnlinePaymentStripe",
                            }),
                            restaurantId: restaurant.id,
                            timestamp: toLocalISOString(now),
                            expiry: Math.floor(now.getTime() / 1000) + EFTPOS_LOG_RETENTION_SECONDS,
                        });
                    }

                    // Delivery is booked asynchronously by the create-order-trigger MODIFY branch.
                    // Navigate with orderId so orderPlaced.tsx can poll for the tracking URL.
                }
            } else {
                const createOrderResult = await createOrder(bugFixTotal, bugFixSubTotal, bugFixSurcharge);
                createdOrderId = createOrderResult.id;
            }

            setShowSpinner(false);
            clearCart();
            navigate(`${orderPlacedPath}`, { state: { orderType, tableNumber, orderScheduledAt, orderId: createdOrderId } });
        } catch (err) {
            console.log("Error in onCreateTransaction: ", err);

            // try {
            //     await sendCustomEmail({
            //         to: "nipoon@tabin.co.nz",
            //         from: {
            //             name: "Tabin Dashboard",
            //             email: "nipoon@tabin.co.nz",
            //         },
            //         subject: `🚨 Online Ordering Error`,
            //         text: JSON.stringify(err),
            //         html: `<div>${JSON.stringify(err)}</div>`,
            //     });
            // } catch (e) {}
            alert(err);
            setShowSpinner(false);
        }
    };

    const orderSummary = (
        <OrderSummary
            onNotesChange={onNotesChange}
            onEditProduct={onEditProduct}
            onUpdateProductQuantity={onUpdateProductQuantity}
            onRemoveProduct={onRemoveProduct}
        />
    );

    const onUpdateProduct = (index: number, product: ICartProduct) => {
        updateProduct(index, product);
        // setShowItemUpdatedModal(true);
    };

    const editProductModal = () => {
        let category: IGET_RESTAURANT_CATEGORY | null = null;
        let product: IGET_RESTAURANT_PRODUCT | null = null;

        if (!productToEdit) {
            return <></>;
        }

        restaurant.categories.items.forEach((c) => {
            if (productToEdit.product.category && c.id == productToEdit.product.category.id) {
                category = c;
            }

            c.products &&
                c.products.items.forEach((p) => {
                    if (p.product.id == productToEdit.product.id) {
                        product = p.product;
                    }
                });
        });

        if (!product || !category) {
            return <></>;
        }

        let orderedModifiers: IPreSelectedModifiers = {};

        productToEdit.product.modifierGroups.forEach((mg) => {
            orderedModifiers[mg.id] = mg.modifiers;
        });

        console.log("orderedModifiers", orderedModifiers);

        return (
            <ProductModal
                category={category}
                product={product}
                isOpen={showEditProductModal}
                onClose={onCloseEditProductModal}
                onUpdateProduct={onUpdateProduct}
                editProduct={{
                    orderedModifiers: orderedModifiers,
                    quantity: productToEdit.product.quantity,
                    notes: productToEdit.product.notes,
                    productCartIndex: productToEdit.displayOrder,
                }}
            />
        );
    };

    const promotionCodeModal = () => {
        return <>{showPromotionCodeModal && <PromotionCodeModal isOpen={showPromotionCodeModal} onClose={onClosePromotionCodeModal} />}</>;
    };

    const thresholdMessageModal = () => {
        return (
            <>
                {showOrderThresholdMessageModal && (
                    <OrderThresholdMessageModal
                        isOpen={showOrderThresholdMessageModal}
                        onClose={onCloseOrderThresholdMessageModal}
                        onContinue={() => setIsShownOrderThresholdMessageModal(true)}
                    />
                )}
            </>
        );
    };

    const orderTypeModal = <>{showOrderTypeModal && <OrderTypeModal isOpen={showOrderTypeModal} onClose={onCloseOrderTypeModal} />}</>;

    // const itemUpdatedModal = (
    //     <>
    //         {showItemUpdatedModal && <ItemAddedUpdatedModal isOpen={showItemUpdatedModal} onClose={onCloseItemUpdatedModal} isProductUpdate={true} />}
    //     </>
    // );

    const modalsAndSpinners = (
        <>
            {showFullScreenSpinner && <FullScreenSpinner show={true} text="Processing your order..." />}

            {editProductModal()}
            {promotionCodeModal()}
            {thresholdMessageModal()}
            {orderTypeModal}
            {/* {itemUpdatedModal} */}
        </>
    );

    const cartEmptyDisplay = (
        <>
            <div className="cart-empty">
                <div>No items selected!</div>
                <div className="separator-2"></div>
            </div>
        </>
    );

    const title = (
        <div className="your-order-title mb-3">
            {/* <CachedImage className="image mr-2" url={`${getPublicCloudFrontDomainName()}/images/shopping-bag-icon.png`} alt="shopping-bag-icon" /> */}
            <div className="h2">Your Order</div>

            {isMobile && (
                <span
                    onClick={() => {
                        navigate(-1);
                    }}
                    className="pt-1 pb-1 pl-1 pr-1"
                >
                    <CloseIcon height={"14px"} className="checkout-close-button" />
                </span>
            )}
        </div>
    );

    const onUpdateCoversNumber = (count: number) => {
        setCovers(count);
    };

    const CoversInput = () => (
        <>
            <div className="mt-3 mb-6">
                <div className="h3 text-bold">Number Of Diners</div>
                <div className="separator-2"></div>
                <div>
                    <Stepper count={covers} min={1} max={20} onUpdate={onUpdateCoversNumber} size={32} />
                </div>
            </div>
        </>
    );

    const orderScheduleTitle = (
        <div ref={orderTimeSectionRefionRef} className="title mb-1 mt-6">
            <div className="h3">Order Time</div>
            <div className="separator-2"></div>
        </div>
    );

    const orderTypeTitle = (
        <div className="title mb-1 mt-6">
            <div className="h3">Order Type</div>
            <div className="separator-2"></div>
        </div>
    );

    const onChangeScheduleDateTime = (dateTimeLocalISO: string | null) => {
        updateOrderScheduledAt(dateTimeLocalISO);
    };

    const orderTitle = (
        <div className="title mb-1 mt-3">
            <div className="h3">Items</div>
            <div className="separator-2"></div>
        </div>
    );

    const restaurantNotes = (
        <>
            <div className="h3 mb-1 mt-6">Special Instructions</div>
            <div className="separator-2"></div>
            <TextArea placeholder={"Leave a note for the restaurant"} value={notes} onChange={onNotesChange} />
        </>
    );

    const order = (
        <>
            {orderSummary}
            {/* {restaurantNotes} */}
        </>
    );

    const paymentTitle = (
        <div className="title mb-1 mt-6">
            <div className="h3">Payment</div>
            <div className="separator-2"></div>
        </div>
    );

    const orderButton = (
        <div>
            <Button className="complete-order-button mt-2" onClick={onCreateTransaction} disabled={products ? products.length == 0 : true}>
                Order
            </Button>
        </div>
    );

    const onClickApplyPromotionCode = async () => {
        setShowPromotionCodeModal(true);
    };

    const onRemoveUserAppliedPromotionCode = () => {
        removeUserAppliedPromotion();
    };

    const promotionInformation = (
        <>
            {promotion && (
                <div className="checkout-promotion-information mb-2 mt-2 mb-2 pt-2 pr-2 pb-2 pl-2">
                    <div>
                        <div className="checkout-promotion-information-heading h5 mb-1">
                            <div>Promotion Applied!</div>
                            <div>-${convertCentsToDollars(promotion.discountedAmount)}</div>
                        </div>
                        {promotion.promotion.type !== EPromotionType.ENTIREORDER ? (
                            <div className="checkout-promotion-information-items">
                                {promotion.promotion.name}:{" "}
                                {Object.values(promotion.matchingProducts).map((p, index) => (
                                    <>
                                        {index !== 0 && ", "}
                                        {p.name}
                                    </>
                                ))}
                            </div>
                        ) : (
                            <div className="checkout-promotion-information-items">Entire Order</div>
                        )}
                    </div>

                    {userAppliedPromotionCode && (
                        <div className="checkout-remove-promotion" onClick={onRemoveUserAppliedPromotionCode}>
                            <CloseIcon height="12px" />
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            <PageWrapper>
                {showSpinner && <FullScreenSpinner show={showSpinner} text="Processing your order..." />}
                <div className="checkout">
                    <div className="order-wrapper">
                        {title}

                        {/* {tableNumber === null && ( */}
                        <div ref={authSectionRef} className="mb-6">
                            <Auth />
                        </div>
                        {/* )} */}

                        {register?.enableCovers && <CoversInput />}

                        {tableNumber === null && (
                            <div>
                                {orderScheduleTitle}
                                <OrderScheduleDateTime
                                    onChange={onChangeScheduleDateTime}
                                    operatingHours={restaurant.operatingHours}
                                    preparationTimeInMinutes={restaurant.preparationTimeInMinutes || 10}
                                />
                            </div>
                        )}

                        <div>
                            {orderTypeTitle}

                            <div className="text-bold mb-3">
                                {orderType} {orderType === EOrderType.DINEIN ? `(Table ${tableNumber})` : ``}{" "}
                                {restaurant.enableOnlineDelivery && <Link onClick={() => setShowOrderTypeModal(true)}>Change</Link>}
                            </div>
                            {orderType === EOrderType.PICKUP ? (
                                <div className="mb-1">{restaurant.name}</div>
                            ) : orderType === EOrderType.DELIVERY ? (
                                <div className="mb-1">Your Delivery Address:</div>
                            ) : null}

                            {orderType === EOrderType.PICKUP ? (
                                <div className="restaurant-address">{restaurant.address.formattedAddress}</div>
                            ) : orderType === EOrderType.DELIVERY ? (
                                <div className="restaurant-address">
                                    {deliveryAddress ? (
                                        <>
                                            <div>{deliveryAddress.street_address.join(", ")}</div>
                                            <div>
                                                {deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zip_code}
                                            </div>
                                        </>
                                    ) : (
                                        "No address available"
                                    )}
                                </div>
                            ) : null}
                        </div>

                        {orderTitle}
                        {promotionInformation}
                        <div className="order">{products && products.length > 0 ? order : cartEmptyDisplay}</div>
                        {/* 
                        {promotion && (
                            <div className="mb-2">
                                {`Discount: -$${convertCentsToDollars(promotion.discountedAmount)}`}{" "}
                                {userAppliedPromotionCode && <Link onClick={onRemoveUserAppliedPromotionCode}>Remove</Link>}
                            </div>
                        )} */}

                        {promotion && (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>{`Discount: (${promotion.promotion.code})`}</div>
                                <div>-${convertCentsToDollars(promotion.discountedAmount)}</div>
                            </div>
                        )}

                        {surcharge ? (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>Surcharge:</div>
                                <div>${convertCentsToDollars(surcharge)}</div>
                            </div>
                        ) : (
                            <></>
                        )}

                        {deliveryFee || deliveryFeeDiscount ? (
                            <div className="checkout-discount-wrapper mb-2 mt-1">
                                <div>Delivery Fee:</div>
                                <div>{deliveryFee === 0 && deliveryFeeDiscount > 0 ? "FREE" : `$${convertCentsToDollars(deliveryFee)}`}</div>
                            </div>
                        ) : null}

                        <div className="checkout-total-wrapper h4">
                            <div>Total:</div>
                            <div>${convertCentsToDollars(subTotal)}</div>
                        </div>

                        {restaurantNotes}

                        {paymentTitle}
                        <div className="mb-3">
                            <Radio
                                className="pay-by-card mb-2"
                                selected={paymentMethod === EPaymentMethod.EFTPOS}
                                onSelect={() => {
                                    setPaymentMethod(EPaymentMethod.EFTPOS);
                                }}
                            >
                                Pay By Card
                            </Radio>

                            {register.enablePayLater && (
                                <Radio
                                    className="pay-at-counter"
                                    selected={paymentMethod === EPaymentMethod.LATER}
                                    onSelect={() => {
                                        setPaymentMethod(EPaymentMethod.LATER);
                                    }}
                                >
                                    Pay Later At Counter
                                </Radio>
                            )}
                        </div>

                        {paymentMethod === EPaymentMethod.EFTPOS && restaurant.onlinePaymentGatewayProvider !== "WINDCAVE" && (
                            <CardElement
                                className="mb-4 stripe-card-element"
                                options={{
                                    hidePostalCode: true,
                                    style: {
                                        base: {
                                            fontSize: "16px",
                                            color: "black",
                                            "::placeholder": {
                                                color: "#aaaaaa",
                                            },
                                        },
                                        invalid: {
                                            color: "#9e2146",
                                        },
                                    },
                                }}
                            />
                        )}

                        <div className="pay-later-link text-center mt-4 mb-3">
                            <Link onClick={onClickApplyPromotionCode}>Apply promo code</Link>
                        </div>
                        <div>{orderButton}</div>
                        <div className="text-center mt-3">
                            <a href="https://www.tabin.co.nz" target="_blank">
                                Powered by tabin.co.nz
                            </a>
                        </div>
                    </div>
                </div>
                {modalsAndSpinners}
            </PageWrapper>
        </>
    );
};

const OrderSummary = (props: {
    onEditProduct: (product: ICartProduct, displayOrder: number) => void;
    onRemoveProduct: (displayOrder: number) => void;
    onUpdateProductQuantity: (displayOrder: number, productQuantity: number) => void;
    onNotesChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) => {
    // context
    const { products } = useCart();

    // displays
    if (!products || products.length === 0) return <h1>No items in cart!</h1>;

    const orderItems = (
        <>
            {products &&
                products.map((product, index) => {
                    // using index as key because products can be duplicated
                    if (product) {
                        return (
                            <div key={index}>
                                <OrderItem
                                    product={product}
                                    displayOrder={index}
                                    onEditProduct={props.onEditProduct}
                                    onUpdateProductQuantity={props.onUpdateProductQuantity}
                                    onRemoveProduct={props.onRemoveProduct}
                                />
                                <div className="separator-2"></div>
                            </div>
                        );
                    }
                })}
        </>
    );

    return <>{orderItems}</>;
};

const OrderItem = (props: {
    product: ICartProduct;
    displayOrder: number;
    onEditProduct: (product: ICartProduct, displayOrder: number) => void;
    onUpdateProductQuantity: (displayOrder: number, productQuantity: number) => void;
    onRemoveProduct: (displayOrder: number) => void;
}) => {
    // constants
    let itemPrice = props.product.price * props.product.quantity;

    props.product.modifierGroups.forEach((mg) => {
        mg.modifiers.forEach((m) => {
            const changedQuantity = m.quantity - m.preSelectedQuantity;

            if (changedQuantity > 0) {
                itemPrice += m.price * changedQuantity * props.product.quantity;
            }
        });
    });

    // displays
    // const quantity = (
    //     <Stepper
    //         count={props.product.quantity}
    //         min={1}
    //         onUpdate={(count: number) => props.onUpdateProductQuantity(props.displayOrder, count)}
    //         size={32}
    //     />
    // );

    return (
        <>
            <div className="order-item">
                {/* {quantity}  */}
                <OrderItemDetails
                    quantity={props.product.quantity}
                    name={props.product.name}
                    price={props.product.price}
                    notes={props.product.notes}
                    modifierGroups={props.product.modifierGroups}
                    // onEditProduct={() => props.onEditProduct(props.product, props.displayOrder)}
                />
                {/* <div className="text-center">
                    <div className="h3 text-primary mb-2">${convertCentsToDollars(itemPrice)}</div>
                    <Button className="remove-button" onClick={() => props.onRemoveProduct(props.displayOrder)}>
                        Remove
                    </Button>
                </div> */}
                {!props.product.isPreSelectedProduct && (
                    <>
                        <Link className="edit-link mt-2 mr-2" onClick={() => props.onEditProduct(props.product, props.displayOrder)}>
                            Edit
                        </Link>
                        <Link className="remove-link mt-2" onClick={() => props.onRemoveProduct(props.displayOrder)}>
                            Remove
                        </Link>
                    </>
                )}
            </div>
        </>
    );
};

const OrderItemDetails = (props: {
    name: string;
    quantity: number;
    price: number;
    notes: string | null;
    modifierGroups: ICartModifierGroup[];
    // onEditProduct: () => void;
}) => {
    const { name, quantity, price, notes, modifierGroups } = props;
    const { isPOS } = useRegister();

    const modifierString = (preSelectedQuantity: number, quantity: number, name: string, price: number) => {
        const changedQuantity = quantity - preSelectedQuantity;
        let mStr = "";

        if (changedQuantity < 0 && Math.abs(changedQuantity) == preSelectedQuantity) {
            mStr = `(REMOVE) ${changedQuantity > 1 ? `${Math.abs(changedQuantity)} x ` : ""}${name}`;
        } else {
            mStr = `${quantity > 1 ? `${Math.abs(quantity)} x ` : ""}${name}`;
        }

        if (price > 0 && changedQuantity > 0) {
            mStr += ` (+$${convertCentsToDollars(price)})`;
        }

        return mStr;
    };

    const nameDisplayString = `${quantity > 1 ? `${quantity} x ` : ""}${name} ($${convertCentsToDollars(price)})`;

    const nameDisplay = (
        <div className="name-edit-button">
            <div className="h4">{nameDisplayString}</div>
        </div>
    );

    const modifiersDisplay = (
        <>
            {modifierGroups.map((mg, index) => (
                <>
                    {!mg.hideForCustomer && (
                        <>
                            <div className="h6 text-bold mt-1" key={mg.id}>
                                {mg.name}
                            </div>
                            {mg.modifiers.map((m) => (
                                <>
                                    <div key={m.id} className="h6 modifier-name">
                                        {modifierString(m.preSelectedQuantity, m.quantity, m.name, m.price)}
                                    </div>
                                    {m.productModifiers && (
                                        <div className="mb-2">
                                            {m.productModifiers.map((productModifier, index) => (
                                                <div>
                                                    <div className="mt-2"></div>
                                                    <ProductModifier
                                                        selectionIndex={m.productModifiers && m.productModifiers.length > 1 ? index + 1 : undefined}
                                                        product={productModifier}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ))}
                        </>
                    )}
                </>
            ))}
        </>
    );

    const notesDisplay = <>{notes && <div className="text-grey mt-2">Notes: {notes}</div>}</>;

    return (
        <div className="detail">
            {nameDisplay}
            {modifiersDisplay}
            {notesDisplay}
        </div>
    );
};
